import './ShoutoutVisibilitySettings.scss';

import React, { memo } from 'react';

import {
  Button,
  Radio,
  Typography,
} from 'antd';
import { IoChevronDownOutline } from 'react-icons/io5';
import { MdClose } from 'react-icons/md';
import TMIcon from 'tm-icons-library';

import CustomPill from '../../../../../components/CustomPill/CustomPill';
import ProfileItem from '../../../../../components/ProfileItem/ProfileItem';
import { useTheme } from '../../../../../context/ThemeProvider';
import {
  TQShoutoutVisibility,
  TQShoutoutVisibilityType,
} from '../../../../../types/tqConnectTypes/user';

const ICON_SIZE = 24;

const BUTTON_SIZE = 48;

export interface ShoutoutSelectedChannel {
  _id: string;
  name: string;
  communityProfilePicture?: string;
  communityProfilePictureType?: string;
}

interface Props {
  visibility: TQShoutoutVisibilityType;
  canPostToCommunity: boolean;
  onVisibilityChange: (visibility: TQShoutoutVisibilityType) => void;
  onDone: () => void;
  /** When false, footer with Done button is hidden (e.g. when used inside a bottom sheet with header Done) */
  showFooter?: boolean;
  /** Selected channels for display (when Share in Community is selected) */
  selectedChannels?: ShoutoutSelectedChannel[];
  /** Called when user taps to open channel picker */
  onSelectChannels?: () => void;
  /** Called when user removes a channel chip */
  onRemoveChannel?: (channelId: string) => void;
}

const ShoutoutVisibilitySettings: React.FC<Props> = ({
  visibility,
  canPostToCommunity,
  onVisibilityChange,
  onDone,
  showFooter = true,
  selectedChannels = [],
  onSelectChannels,
  onRemoveChannel,
}) => {
  const { colors: COLORS } = useTheme();

  const showSelectChannelSection =
    canPostToCommunity &&
    visibility === TQShoutoutVisibility.CHANNEL_ONLY &&
    typeof onSelectChannels === 'function';

  const displayCount =
    selectedChannels.length > 99 ? '99+' : selectedChannels.length;

  return (
    <div className="modal__content shoutout-visibility-settings">
      <div className="modal__content__body" style={{ overflow: 'hidden' }}>
        <Typography.Title level={5} className="content__label">
          Where to post shoutout?
        </Typography.Title>

        <Radio.Group
          value={visibility}
          onChange={(e) => onVisibilityChange(e.target.value)}>
          <Radio value={TQShoutoutVisibility.PROFILE_ONLY}>
            <ProfileItem
              title="Profile only"
              subtitle="Personal shoutout"
              avatar={
                <CustomPill
                  shape="circle"
                  text={
                    <TMIcon
                      name="person"
                      size={ICON_SIZE}
                      color={COLORS.ICON}
                      style={{ flexShrink: 0 }}
                    />
                  }
                  width={BUTTON_SIZE}
                  height={BUTTON_SIZE}
                  bgColor={COLORS.BACKGROUND}
                  disableHoverEffect
                />
              }
              titleStyle={{
                fontSize: 14,
              }}
            />
          </Radio>

          {canPostToCommunity ? (
            <Radio value={TQShoutoutVisibility.CHANNEL_ONLY}>
              <ProfileItem
                title="Share in Community"
                subtitle="Community shoutout"
                avatar={
                  <CustomPill
                    shape="circle"
                    text={
                      <TMIcon
                        name="people"
                        size={ICON_SIZE}
                        color={COLORS.ICON}
                        style={{ flexShrink: 0 }}
                      />
                    }
                    width={BUTTON_SIZE}
                    height={BUTTON_SIZE}
                    bgColor={COLORS.BACKGROUND}
                    disableHoverEffect
                  />
                }
                titleStyle={{
                  fontSize: 14,
                }}
              />
            </Radio>
          ) : null}
        </Radio.Group>

        {showSelectChannelSection ? (
          <div className="shoutout-visibility-settings__select-channel">
            <Typography.Title
              level={5}
              className="shoutout-visibility-settings__select-channel-label">
              Select channel
            </Typography.Title>
            <button
              type="button"
              className="shoutout-visibility-settings__select-channel-input"
              onClick={onSelectChannels}
              aria-label="Select channel">
              <div className="shoutout-visibility-settings__select-channel-chips">
                {selectedChannels.length === 0 ? (
                  <span className="shoutout-visibility-settings__select-channel-placeholder">
                    Select channel
                  </span>
                ) : (
                  selectedChannels.map((channel) => (
                    <span
                      key={channel._id}
                      className="shoutout-visibility-settings__select-channel-chip">
                      {channel.communityProfilePictureType === 'emoji' ? (
                        <span className="shoutout-visibility-settings__select-channel-chip-icon shoutout-visibility-settings__select-channel-chip-icon--emoji">
                          {channel.communityProfilePicture}
                        </span>
                      ) : channel.communityProfilePicture ? (
                        <span className="shoutout-visibility-settings__select-channel-chip-icon">
                          <img
                            src={channel.communityProfilePicture}
                            alt=""
                            width={24}
                            height={24}
                          />
                        </span>
                      ) : null}
                      <span className="shoutout-visibility-settings__select-channel-chip-name">
                        {channel.name}
                      </span>
                      {typeof onRemoveChannel === 'function' ? (
                        <button
                          type="button"
                          className="shoutout-visibility-settings__select-channel-chip-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveChannel(channel._id);
                          }}
                          aria-label={`Remove ${channel.name}`}>
                          <MdClose size={16} color={COLORS.ICON} />
                        </button>
                      ) : null}
                    </span>
                  ))
                )}
              </div>
              <span className="shoutout-visibility-settings__select-channel-chevron">
                {selectedChannels.length > 0 ? (
                  <span className="shoutout-visibility-settings__select-channel-count">
                    {displayCount}
                  </span>
                ) : null}
                <IoChevronDownOutline size={20} color={COLORS.ICON} />
              </span>
            </button>
          </div>
        ) : null}
      </div>

      {showFooter ? (
        <div className="modal__content__footer">
          <Button className="siteBtn accentBtnV1" onClick={onDone} block>
            Done
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default memo(ShoutoutVisibilitySettings);
